import React, { useMemo, useEffect, useState, useCallback } from "react";
import * as R from "ramda";
import { Modal, Spin } from "antd";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import * as gtag from "../../lib/gtag";
import {
  FETCH_UNIVERSITY_COMPANIES,
  fetchUniversityCompanies
} from "../../store/actions/catalogs";
import { linkToCompanies } from "../../store/actions/company";
import { isLoading } from "../../utils/actions";
import {
  saveOpportunityMultipostingUniversity,
  SAVE_MULTIPOSTING_UNIVERSITIES_FORM
} from "../../store/actions/opportunity";
import Button from "../Button";

import VinculationsTable, { vinculationStatuses } from "./VinculationsTable";

import styled from "styled-components/macro";

const LinkModal = styled.div`
  text-align: center;
  margin-bottom: -16px;

  h3 {
    margin-bottom: 24px;
  }
  p {
    text-align: left;
  }

  button {
    max-width: 220px;
    width: 100%;
  }
`;

const VinculationButtonWrapper = styled.div`
  padding-top: 24px;
  display: flex;
  justify-content: center;
`;

const CenteredSpin = () => (
  <div style={{ width: "100%", textAlign: "center" }}>
    <Spin />
  </div>
);

const listTypes = {
  wizard: "wizard",
  list: "list",
  details: "details"
};

const UniversityList = ({
  type,
  universities,
  vinculations,
  company,
  fetchUniversityCompanies,
  loading,
  linkToCompanies,
  onChangeSelected,
  initialSelected,
  isDisabled,
  opportunity,
  saving,
  saveOpportunityMultipostingUniversity,
  section,
  newOpportunity,
  setForm
}) => {
  const { t } = useTranslation(["components", "global", "vinculations"]);
  const [publishedOnCompanies, setPublishedOnCompanies] = useState([]);

  useEffect(() => {
    fetchUniversityCompanies();
  }, [fetchUniversityCompanies]);

  const saveActions = useMemo(() => {
    if (type !== listTypes.details) return;

    const actions = [];
    publishedOnCompanies.forEach(({ id }) => {
      if (!(initialSelected || []).includes(id)) {
        actions.push({ idCompany: id, enabled: true });
      }
    });
    (initialSelected || []).forEach(id => {
      if (!publishedOnCompanies.find(({ id: idCompany }) => id === idCompany)) {
        actions.push({ idCompany: id, enabled: false });
      }
    });
    return actions;
  }, [initialSelected, publishedOnCompanies, type]);

  const savePublishedOnCompanies = useCallback(() => {
    gtag.event({
      category: "VacancyDetail",
      action: "VacancyDetail.University-Save",
      label: opportunity.id
    });
    saveOpportunityMultipostingUniversity({
      publishedOnCompanies: saveActions,
      id: opportunity.id
    });
  }, [opportunity, saveActions, saveOpportunityMultipostingUniversity]);

  useEffect(() => {
    setForm && setForm({ publishedOnCompanies: saveActions });
  }, [saveActions, setForm]);

  const data = useMemo(() => {
    const formattedUniversities = universities
      .filter(({ id }) => id !== company.id)
      .map(({ id, ...university }) => {
        const vinculation = R.find(({ idCompany }) => idCompany === id)(
          vinculations
        );
        return {
          ...university,
          id,
          status: (vinculation || {}).status || vinculationStatuses.unlinked,
          reason: (vinculation || {}).reason
        };
      });

    if (type !== listTypes.list) {
      return formattedUniversities.filter(
        ({ status }) => status === vinculationStatuses.linked
      );
    }

    return formattedUniversities;
  }, [company.id, type, universities, vinculations]);

  const linkUniversities = async universities => {
    const modal = Modal.info({
      icon: null,
      okButtonProps: { style: { display: "none" } },
      content: <CenteredSpin />,
      maskClosable: false
    });

    try {
      await linkToCompanies(
        universities.map(({ id }) => ({
          idCompany: id,
          status: vinculationStatuses.pending
        }))
      );

      modal.update({
        icon: null,
        okButtonProps: { style: { display: "none" } },
        content: (
          <LinkModal>
            <h3>
              {t("components:UniversityList.requestSent")}{" "}
              <strong>{company.name}</strong>{" "}
              {t("components:UniversityList.with")}{" "}
              <strong>
                {universities.length > 1
                  ? t("components:UniversityList.universities", {
                      amount: universities.length
                    })
                  : universities[0].name}
              </strong>
              .
            </h3>
            <p>
              {universities.length > 1
                ? t("components:UniversityList.notifyUniversities")
                : t("components:UniversityList.notifyUniversity")}
              <br /> <br />
              {/* {t("components:UniversityList.noWorries")} */}
            </p>
            <Button onClick={() => modal.destroy()} type="primary">
              {t("global:continue")}
            </Button>
          </LinkModal>
        )
      });
    } catch (e) {
      console.log({ e });
      modal.destroy();
    }
  };

  const unlinkUniversities = universities => {
    const modal = Modal.confirm({
      title: t("vinculations:sureUnlink", {
        amount:
          universities.length > 1
            ? t("vinculations:multipleUniversities", {
                amount: universities.length
              })
            : `${universities[0].name}`
      }),
      okButtonProps: { style: { display: "none" } },
      cancelButtonProps: { style: { display: "none" } },
      content: (
        <div style={{ marginBottom: -12, marginTop: 8 }}>
          <Button
            type="quaternary"
            onClick={() => modal.destroy()}
            style={{ marginRight: 8 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              modal.update({
                content: <CenteredSpin />
              });
              linkToCompanies(
                universities.map(({ id }) => ({
                  idCompany: id,
                  status: vinculationStatuses.unlinked
                }))
              ).finally(modal.destroy);
            }}
            type="primary"
          >
            {t("vinculations:unlink")}
          </Button>
        </div>
      )
    });
  };

  return (
    <>
      <VinculationsTable
        section={section}
        type="universities"
        data={data}
        onLink={linkUniversities}
        onUnlink={unlinkUniversities}
        loading={loading}
        isDisabled={isDisabled}
        allowLink={type === listTypes.list}
        disableCheckboxFilter={status =>
          status === vinculationStatuses.rejected ||
          status === vinculationStatuses.pending
        }
        tableMargin={type === listTypes.wizard ? null : undefined}
        onChangeSelected={
          type === listTypes.wizard ? onChangeSelected : setPublishedOnCompanies
        }
        initialSelected={initialSelected}
        tabs={
          type === listTypes.list && [
            {
              name: t("vinculations:filters.all"),
              icon: "unordered-list",
              default: true,
              "data-test": "filter-all"
            },
            {
              name: t("vinculations:filters.linked"),
              icon: "check-circle",
              filter: R.propEq("status", vinculationStatuses.linked),
              "data-test": "filter-linked"
            },
            {
              name: t("vinculations:filters.pending"),
              icon: "clock-circle",
              filter: R.propEq("status", vinculationStatuses.pending),
              "data-test": "filter-pending"
            },
            {
              name: t("vinculations:filters.rejected"),
              icon: "close-circle",
              filter: R.propEq("status", vinculationStatuses.rejected),
              "data-test": "filter-rejected"
            }
          ]
        }
        translateKey="universities"
        data-test="university-list"
      />
      {(type === listTypes.details || type === listTypes.wizard) &&
        data.length > 0 && (
          <VinculationButtonWrapper>
            <Link to="/compañia/universidades">
              <Button icon="link">{t("vinculations:linkNew")}</Button>
            </Link>
          </VinculationButtonWrapper>
        )}
      {type === listTypes.details && newOpportunity && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 12,
            width: "100%"
          }}
        >
          <Button
            onClick={savePublishedOnCompanies}
            type="primary"
            size="lg"
            disabled={isDisabled || saving || saveActions.length < 1}
            loading={saving}
          >
            {t("global:save")}
          </Button>
        </div>
      )}
    </>
  );
};

UniversityList.propTypes = {
  type: PropTypes.oneOf(Object.values(listTypes))
};

export default connect(
  state => ({
    newOpportunity: state.opportunity.newOpportunity,
    universities: R.or(FETCH_UNIVERSITY_COMPANIES.getResponse(state), []),
    company: R.propOr({}, "company", state),
    error: FETCH_UNIVERSITY_COMPANIES.getError(state),
    loading: isLoading(FETCH_UNIVERSITY_COMPANIES, state),
    saving: isLoading(SAVE_MULTIPOSTING_UNIVERSITIES_FORM, state),
    vinculations: R.pathOr(
      [],
      ["company", "companyVinculations", "requiredFromMe"],
      state
    )
  }),
  {
    fetchUniversityCompanies,
    linkToCompanies,
    saveOpportunityMultipostingUniversity
  }
)(UniversityList);
